// ========================================
// MODAL UTILITIES
// Scroll-lock + shared helpers (presentation)
// ========================================

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/** Fired on shell route change so portaled keep-alive modals can close. */
export const ROUTE_DISMISS_MODALS_EVENT = 'aman:route-dismiss-modals';

export function dismissModalsOnRouteChange(pathname = '') {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent(ROUTE_DISMISS_MODALS_EVENT, { detail: { pathname } })
    );
  } catch (_) {
    /* ignore */
  }
}

let openCount = 0;
let savedWindowScrollY = 0;
let savedMainScrollTop = 0;
/** @type {HTMLElement[]} */
let lockedScrollEls = [];

const SCROLL_LOCK_SELECTORS = [
  '.main-content',
  '.page-content',
  '.layout .main-content',
  '.ads-shell .main-content',
];

function collectScrollContainers() {
  const seen = new Set();
  const els = [];
  for (const sel of SCROLL_LOCK_SELECTORS) {
    document.querySelectorAll(sel).forEach((node) => {
      if (!(node instanceof HTMLElement) || seen.has(node)) return;
      seen.add(node);
      els.push(node);
    });
  }
  return els;
}

export function releaseStuckScrollLock() {
  openCount = 0;
  runModalSafetyCheck();
}

/** Live body portals + shell overlays that must keep scroll lock. */
export const LIVE_MODAL_PORTAL_SELECTOR = [
  '.ads-dialog-backdrop',
  '.modal-backdrop',
  '.modal-overlay',
  '.new-cash-register-modal-overlay',
  '.wdm-overlay',
  '.cem-overlay',
  '.cem-lockout-overlay',
  '.pos-modal-root',
  '.pos-scanner-presence-host',
  '.barcode-scanner-modal-fullscreen',
  '.pharm-modal-overlay',
  '.pp-overlay',
  '.session-details-overlay',
  '.location-modal-overlay',
  'dialog[open]',
].join(', ');

export function hasLiveModalPortal() {
  if (typeof document === 'undefined') return false;
  return document.querySelector(LIVE_MODAL_PORTAL_SELECTOR) !== null;
}

export function isModalLockPossiblyActive() {
  if (typeof document === 'undefined') return openCount > 0;
  if (openCount > 0) return true;
  const root = document.documentElement;
  const body = document.body;
  return (
    root.classList.contains('ads-modal-open') ||
    root.classList.contains('modal-open') ||
    body.classList.contains('ads-modal-open') ||
    body.classList.contains('modal-open') ||
    hasLiveModalPortal()
  );
}

/** Unlock only when no live portal / lock class remains. */
export function releaseScrollLockOnRouteChange() {
  if (!isModalLockPossiblyActive()) return;
  if (hasLiveModalPortal()) return;
  releaseStuckScrollLock();
}

/**
 * Foolproof safety check to clean up stuck scroll locks if no modal is in the DOM.
 */
export function runModalSafetyCheck() {
  if (typeof document === 'undefined') return;

  const hasActiveModal = hasLiveModalPortal();

  if (!hasActiveModal) {
    openCount = 0;

    // Restore scroll containers in reference list
    lockedScrollEls.forEach((el) => {
      if (el) {
        el.style.overflow = el.dataset.adsPrevOverflow || '';
        delete el.dataset.adsPrevOverflow;
      }
    });
    lockedScrollEls = [];

    // Also scan DOM directly to restore any leftover containers
    SCROLL_LOCK_SELECTORS.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.overflow = el.dataset.adsPrevOverflow || '';
          delete el.dataset.adsPrevOverflow;
        }
      });
    });

    // Remove locking classes from document root and body
    document.documentElement.classList.remove('ads-modal-open', 'modal-open');
    document.body.classList.remove('ads-modal-open', 'modal-open');
    document.body.style.top = '';
  }
}

// Global safety hooks on browser navigation (no document click — thrash on POS taps)
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => setTimeout(runModalSafetyCheck, 150));
  window.addEventListener('hashchange', () => setTimeout(runModalSafetyCheck, 150));
}

/**
 * Refcounted lock for window + app shell scroll containers (.main-content).
 * @returns {() => void}
 */
export const acquireModalScrollLock = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => {};
  }

  if (openCount === 0) {
    savedWindowScrollY = window.scrollY || window.pageYOffset || 0;
    lockedScrollEls = collectScrollContainers();
    const main = lockedScrollEls.find((el) => el.classList.contains('main-content'));
    savedMainScrollTop = main ? main.scrollTop : 0;

    document.documentElement.classList.add('ads-modal-open', 'modal-open');
    document.body.classList.add('ads-modal-open', 'modal-open');

    lockedScrollEls.forEach((el) => {
      el.dataset.adsPrevOverflow = el.style.overflow || '';
      el.style.overflow = 'hidden';
    });
  }
  openCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    openCount = Math.max(0, openCount - 1);
    
    // Defer check to let React transition and DOM state settle
    setTimeout(runModalSafetyCheck, 150);

    if (openCount > 0) return;

    lockedScrollEls.forEach((el) => {
      el.style.overflow = el.dataset.adsPrevOverflow || '';
      delete el.dataset.adsPrevOverflow;
    });
    lockedScrollEls = [];

    document.documentElement.classList.remove('ads-modal-open', 'modal-open');
    document.body.classList.remove('ads-modal-open', 'modal-open');
    document.body.style.top = '';

    const main = document.querySelector('.main-content');
    if (main instanceof HTMLElement) {
      main.scrollTop = savedMainScrollTop;
    }
    window.scrollTo(0, savedWindowScrollY);
  };
};

/**
 * Legacy sync API — prefer acquireModalScrollLock in effects.
 * @param {boolean} isOpen
 */
export const setModalOpen = (isOpen) => {
  if (typeof document === 'undefined') return;
  if (isOpen) {
    document.body.classList.add('modal-open', 'ads-modal-open');
    document.documentElement.classList.add('modal-open', 'ads-modal-open');
  } else if (openCount === 0) {
    document.body.classList.remove('modal-open', 'ads-modal-open');
    document.documentElement.classList.remove('modal-open', 'ads-modal-open');
    document.body.style.top = '';
  }
};

/**
 * @param {boolean} isOpen
 * @param {function} onClose
 * @returns {function} Cleanup
 */
export const createEscKeyHandler = (isOpen, onClose) => {
  const handleEscKey = (event) => {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  };

  if (isOpen) {
    document.addEventListener('keydown', handleEscKey);
  }

  return () => {
    document.removeEventListener('keydown', handleEscKey);
  };
};

/**
 * @param {boolean} isOpen
 * @param {function} onClose
 */
export const useModal = (isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    return acquireModalScrollLock();
  }, [isOpen]);

  useEffect(() => createEscKeyHandler(isOpen, onClose), [isOpen, onClose]);
};

/**
 * Portal helper — always mount overlays on document.body.
 * @param {import('react').ReactNode} node
 * @param {boolean} open
 */
export const portalToBody = (node, open = true) => {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};
